import React, { useState, useMemo } from 'react';
import {
  Building, Users, TrendingUp, Award, Filter, X, ChevronDown, Grid3x3, List,
  FileText, Cloud, Loader2, Mail, CheckSquare, Square
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, toast } from '@clareefai/ui';
import {
  useGetPromotersQuery,
  useGetImportedContactsQuery,
  useInviteImportedContactMutation,
  useInviteImportedContactsBatchMutation,
  useAssignImportedContactsMutation,
} from '../../store/promoterApi';
import { Promoter, ImportedContact } from '@clareefai/types';
import type { PromoterResponse } from '../../store/promoterApi';
import { useGetTeamMembersQuery } from '../../store/teamMemberApi';
import { useAbility } from '../../contexts/AbilityContext';
import AssignImportedContactsModal from '../../components/promoters/AssignImportedContactsModal';
import { WidgetBadge, ApproveWidgetAction } from '../../components/promoters/WidgetApproval';

type DerivedProgress = {
  reviewCompleted: boolean;
  testimonialCompleted: boolean;
  useCaseCompleted: boolean;
  firstMeetingCompleted: boolean;
};

/** Derive progress from what promoters added/uploaded (testimonials) and calls (meetings). */
const deriveProgressFromTestimonialsAndMeetings = (response: PromoterResponse): DerivedProgress => {
  const testimonials = response.testimonials ?? [];
  const meetings = response.meetings ?? [];

  const reviewCompleted = testimonials.some((t) => t.type === 'REVIEW');
  const testimonialCompleted = testimonials.some((t) => t.type === 'VIDEO');
  const useCaseCompleted = testimonials.some((t) => t.type === 'USE_CASE');

  const firstMeetingCompleted = meetings.some((m) => {
    const s = (m.status ?? '').toUpperCase();
    if (s === 'COMPLETED' || s === 'PARTIALLY_ATTENDED') return true;
    return !!(m.meeting_started_at ?? m.prospect_joined_at ?? m.promoter_joined_at);
  });

  return {
    reviewCompleted,
    testimonialCompleted,
    useCaseCompleted,
    firstMeetingCompleted,
  };
};

// Helper function to map backend PromoterResponse to frontend Promoter
const mapPromoterResponseToPromoter = (response: PromoterResponse): Promoter => {
  // Determine status based on derived progress from testimonials and meetings
  let status: Promoter['status'] = 'invited';

  const { reviewCompleted, testimonialCompleted, useCaseCompleted, firstMeetingCompleted } =
    deriveProgressFromTestimonialsAndMeetings(response);

  if (firstMeetingCompleted && useCaseCompleted && testimonialCompleted && reviewCompleted) {
    status = 'available';
  } else if (useCaseCompleted && testimonialCompleted && reviewCompleted) {
    status = 'testimonial_done';
  } else if (testimonialCompleted && reviewCompleted) {
    status = 'testimonial_ready';
  } else if (reviewCompleted) {
    status = 'review';
  } else if (response.status) {
    // Fallback to status field if no testimonials/meetings found
    const statusMap: Record<string, Promoter['status']> = {
      'invited': 'invited',
      'review': 'review',
      'testimonial_ready': 'testimonial_ready',
      'testimonial_done': 'testimonial_done',
      'available': 'available'
    };
    status = statusMap[response.status] || 'invited';
  }

  const importSource = (response.importSource as Promoter['importSource']) ?? null;

  return {
    id: response.id || '',
    name: `${response.firstName} ${response.lastName}`,
    email: response.email,
    phone: response.phone || '',
    company: response.company || '',
    avatar: response.avatar,
    status,
    prospectsReferred: response.prospectsReferred || 0,
    conversionRate: response.conversionRate || 0,
    totalRewards: response.totalRewards || 0,
    joinedDate: response.joinedDate || response.createdAt || '',
    importSource,
    createdByType: response.createdByType,
    approvalStatus: response.approvalStatus ?? null,
  };
};

type ListItem =
  | ({ _type: 'promoter' } & Promoter)
  | ({ _type: 'imported' } & ImportedContact & { name: string; company: string });

const ImportSourceBadge: React.FC<{ source?: Promoter['importSource'] | 'csv' | 'hubspot' }> = ({ source }) => {
  const { t } = useTranslation();
  if (!source || source === 'manual') return null;

  if (source === 'csv') {
    return (
      <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
        <FileText className="w-3 h-3" />
        <span>{t('promoters.list.csvImport')}</span>
      </span>
    );
  }

  if (source === 'hubspot') {
    return (
      <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
        <Cloud className="w-3 h-3" />
        <span>{t('promoters.list.hubspot')}</span>
      </span>
    );
  }

  return null;
};

const PromotersList: React.FC = () => {
  const { t } = useTranslation();
  const { data: promotersData, isLoading, error } = useGetPromotersQuery();
  const { data: importedContactsData } = useGetImportedContactsQuery();
  const { data: teamMembersData = [] } = useGetTeamMembersQuery();
  const [inviteContact, { isLoading: isInvitingSingle }] = useInviteImportedContactMutation();
  const [inviteBatch, { isLoading: isInvitingBatch }] = useInviteImportedContactsBatchMutation();
  const [assignContacts, { isLoading: isAssigning }] = useAssignImportedContactsMutation();
  const ability = useAbility();
  const canAssign = ability.can('update', 'TeamMember');
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [selectedImported, setSelectedImported] = useState<string[]>([]);
  const [invitingId, setInvitingId] = useState<string | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);

  // Map backend data to frontend format
  const promoters = useMemo(() => {
    if (!promotersData) return [];
    return promotersData.map(mapPromoterResponseToPromoter);
  }, [promotersData]);

  // Map imported contacts to ListItem shape
  const importedItems: ListItem[] = (importedContactsData ?? []).map((c) => ({
    _type: 'imported' as const,
    ...c,
    name: `${c.firstName} ${c.lastName}`,
    company: c.company ?? '',
  }));

  const promoterItems: ListItem[] = promoters.map((p) => ({ _type: 'promoter' as const, ...p }));

  // États des filtres
  const [filters, setFilters] = useState({
    companySize: '',
    sectors: [] as string[],
    company: '',
    status: ''
  });
  const [repFilter, setRepFilter] = useState('');

  const getStatusBadge = (status: string) => {
    if (status === 'imported') {
      return (
        <span className="status-badge bg-gray-100 text-gray-700 border border-gray-300">
          {t('promoters.list.statusImported')}
        </span>
      );
    }

    const badges = {
      invited: 'status-invited',
      review: 'status-review',
      testimonial_ready: 'status-available',
      testimonial_done: 'status-completed',
      available: 'status-available'
    };

    const labels = {
      invited: t('promoters.kanbanBoard.status.invited'),
      review: t('promoters.kanbanBoard.status.reviewed'),
      testimonial_ready: t('promoters.kanbanBoard.status.testimonialReady'),
      testimonial_done: t('promoters.kanbanBoard.status.testimonialDone'),
      available: t('promoters.kanbanBoard.status.available')
    };

    return (
      <span className={`status-badge ${badges[status as keyof typeof badges]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  const handlePromoterClick = (promoterId: string) => {
    navigate(`/promoters/${promoterId}`);
  };

  const handleInviteSingle = async (e: React.MouseEvent, contactId: string) => {
    e.stopPropagation();
    setInvitingId(contactId);
    try {
      await inviteContact(contactId).unwrap();
      setSelectedImported(prev => prev.filter(id => id !== contactId));
      toast.success(t('promoters.list.inviteSuccess'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('promoters.list.inviteFailed'));
    } finally {
      setInvitingId(null);
    }
  };

  const handleInviteSelected = async () => {
    if (selectedImported.length === 0) return;
    try {
      const result = await inviteBatch({ ids: selectedImported }).unwrap();
      setSelectedImported([]);
      if (result.error_count > 0) {
        const errorMessages = result.results
          .filter((r) => r.status === 'error')
          .map((r) => r.error || t('common.unknownError'));
        toast.error(t('promoters.list.batchFailedSome', { count: result.error_count, errors: errorMessages.join(', ') }));
      }
      if (result.success_count > 0) {
        toast.success(t('promoters.list.batchSuccess', { count: result.success_count }));
      }
    } catch (err: any) {
      toast.error(err?.data?.message || t('promoters.list.batchFailed'));
    }
  };

  const handleAssignToMember = async (teamMemberId: string) => {
    try {
      await assignContacts({ contactIds: selectedImported, teamMemberId }).unwrap();
      setSelectedImported([]);
      setShowAssignModal(false);
      toast.success(t('promoters.list.assignSuccess'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('promoters.list.assignFailed'));
    }
  };

  const toggleSelectImported = (id: string) => {
    setSelectedImported(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Get sectors from backend data or use static fallback
  const getPromoterSectors = (promoterId: string) => {
    const promoter = promotersData?.find(p => p.id === promoterId);
    if (promoter?.industrySectors && promoter.industrySectors.length > 0) {
      return promoter.industrySectors;
    }
    // Fallback to static data
    const sectorsByPromoter: { [key: string]: string[] } = {
      '1': ['Technologie', 'Services'],
      '2': ['Marketing', 'Consulting'],
      '3': ['Consulting', 'Finance'],
      '4': ['Technologie', 'Innovation'],
      '5': ['Technologie', 'Startup'],
      '6': ['Finance', 'Technologie'],
      '7': ['Commerce', 'E-commerce'],
      '8': ['Santé', 'Technologie'],
      '9': ['Transport', 'Logistique'],
      '10': ['Éducation', 'Technologie'],
      '11': ['Industrie', 'Manufacturing'],
      '12': ['Commerce', 'Retail'],
      '13': ['Énergie', 'Environnement'],
      '14': ['Média', 'Digital'],
      '15': ['Transport', 'Technologie'],
      '16': ['Consulting', 'Business'],
      '17': ['Technologie', 'SaaS'],
      '18': ['Santé', 'Biotechnologie'],
      '19': ['Automobile', 'Technologie'],
      '20': ['Mode', 'Technologie'],
      '21': ['Cybersécurité', 'Technologie'],
      '22': ['Marketing', 'Digital'],
      '23': ['Blockchain', 'Finance'],
      '24': ['Intelligence Artificielle', 'Technologie'],
      '25': ['Robotique', 'Innovation'],
      'hs-1': ['CRM', 'Ventes'],
      'hs-2': ['Cloud', 'Technologie'],
      'hs-3': ['Base de données', 'Enterprise'],
      'hs-4': ['Design', 'Créatif'],
      'hs-5': ['Marketing', 'Inbound'],
      'hs-6': ['Support', 'Service client'],
      'hs-7': ['Communication', 'Collaboration'],
      'hs-8': ['Gestion de projet', 'Développement'],
      'hs-9': ['E-commerce', 'Retail'],
      'hs-10': ['Communication', 'API']
    };
    return sectorsByPromoter[promoterId] || ['Technologie', 'Services'];
  };

  const getCompanySize = (promoterId: string) => {
    const promoter = promotersData?.find(p => p.id === promoterId);
    if (promoter?.companySize) {
      return promoter.companySize;
    }
    // Fallback to static data
    const sizesByPromoter: { [key: string]: string } = {
      '1': '51-200 employés',
      '2': '11-50 employés',
      '3': '201-500 employés',
      '4': '11-50 employés',
      '5': '51-200 employés',
      '6': '11-50 employés',
      '7': '51-200 employés',
      '8': '201-500 employés',
      '9': '51-200 employés',
      '10': '201-500 employés',
      '11': '51-200 employés',
      '12': '11-50 employés',
      '13': '501-1000 employés',
      '14': '201-500 employés',
      '15': '501-1000 employés',
      '16': '11-50 employés',
      '17': '51-200 employés',
      '18': '1000+ employés',
      '19': '201-500 employés',
      '20': '51-200 employés',
      '21': '11-50 employés',
      '22': '51-200 employés',
      '23': '201-500 employés',
      '24': '1000+ employés',
      '25': '501-1000 employés',
      'hs-1': '1000+ employés',
      'hs-2': '1000+ employés',
      'hs-3': '1000+ employés',
      'hs-4': '1000+ employés',
      'hs-5': '501-1000 employés',
      'hs-6': '201-500 employés',
      'hs-7': '501-1000 employés',
      'hs-8': '501-1000 employés',
      'hs-9': '201-500 employés',
      'hs-10': '201-500 employés'
    };
    return sizesByPromoter[promoterId] || '51-200 employés';
  };

  // Options pour les filtres
  const companySizeOptions = [
    '1-10 employés',
    '11-50 employés',
    '51-200 employés',
    '201-500 employés',
    '501-1000 employés',
    '1000+ employés'
  ];

  const sectorOptions = [
    'Technologie', 'Finance', 'Santé', 'Éducation', 'Commerce', 'Industrie',
    'Services', 'Marketing', 'Consulting', 'Innovation', 'Startup', 'E-commerce',
    'Transport', 'Logistique', 'Manufacturing', 'Retail', 'Énergie', 'Environnement',
    'Média', 'Digital', 'Business', 'SaaS', 'Biotechnologie', 'Automobile',
    'Mode', 'Cybersécurité', 'Blockchain', 'Intelligence Artificielle', 'Robotique',
    'CRM', 'Ventes', 'Cloud', 'Enterprise', 'Base de données', 'Design', 'Créatif',
    'Inbound', 'Support', 'Service client', 'Communication', 'Collaboration',
    'Gestion de projet', 'Développement', 'API'
  ];

  const statusOptions = [
    { value: 'imported', label: t('promoters.list.statusImported') },
    { value: 'invited', label: t('promoters.kanbanBoard.status.invited') },
    { value: 'review', label: t('promoters.kanbanBoard.status.reviewed') },
    { value: 'testimonial_ready', label: t('promoters.kanbanBoard.status.testimonialReady') },
    { value: 'testimonial_done', label: t('promoters.kanbanBoard.status.testimonialDone') },
    { value: 'available', label: t('promoters.kanbanBoard.status.available') }
  ];

  const companyOptions = useMemo(() => {
    return [...new Set(promoters.map(p => p.company))].sort();
  }, [promoters]);

  // Merge imported at top + filter
  const allItems: ListItem[] = [...importedItems, ...promoterItems];

  const filteredItems = allItems.filter(item => {
    if (filters.status === 'imported' && item._type !== 'imported') return false;
    if (filters.status && filters.status !== 'imported' && item._type === 'imported') return false;
    if (item._type === 'imported') return true; // imported contacts pass all other filters

    const promoter = item as ListItem & { _type: 'promoter' };

    const promoterSectors = getPromoterSectors(promoter.id);
    const promoterCompanySize = getCompanySize(promoter.id);

    if (filters.companySize && promoterCompanySize !== filters.companySize) return false;
    if (filters.sectors.length > 0) {
      const hasMatchingSector = filters.sectors.some(sector => promoterSectors.includes(sector));
      if (!hasMatchingSector) return false;
    }
    if (filters.company && promoter.company !== filters.company) return false;
    if (filters.status && promoter.status !== filters.status) return false;
    if (repFilter) {
      const raw = promotersData?.find(p => p.id === promoter.id);
      if (raw?.assignedTeamMember?.id !== repFilter) return false;
    }

    return true;
  });

  // Gestion des filtres
  const handleFilterChange = (filterType: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleSectorToggle = (sector: string) => {
    setFilters(prev => ({
      ...prev,
      sectors: prev.sectors.includes(sector)
        ? prev.sectors.filter(s => s !== sector)
        : [...prev.sectors, sector]
    }));
  };

  const clearFilters = () => {
    setFilters({
      companySize: '',
      sectors: [],
      company: '',
      status: ''
    });
    setRepFilter('');
  };

  const hasActiveFilters = filters.companySize || filters.sectors.length > 0 || filters.company || filters.status || repFilter;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">{t('promoters.kanbanBoard.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-500">{t('promoters.kanbanBoard.loadError')}</div>
      </div>
    );
  }

  return (
    <>
      {/* Sticky "Invite Selected" bar */}
      {selectedImported.length > 0 && (
        <div className="sticky top-0 z-10 bg-[#4339A0] text-white px-6 py-3 flex items-center justify-between shadow-md">
          <span className="font-medium">{t('promoters.list.contactsSelected', { count: selectedImported.length })}</span>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setSelectedImported([])}
              className="text-white/70 hover:text-white text-sm"
            >
              {t('promoters.list.clearSelection')}
            </button>
            {canAssign && (
              <button
                onClick={() => setShowAssignModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-white/20 text-white border border-white/40 rounded-lg font-semibold text-sm hover:bg-white/30 transition-colors"
              >
                <Users className="w-4 h-4" />
                <span>{t('promoters.list.assignToMember')}</span>
              </button>
            )}
            <button
              onClick={handleInviteSelected}
              disabled={isInvitingBatch}
              className="flex items-center space-x-2 px-4 py-2 bg-white text-[#4339A0] rounded-lg font-semibold text-sm hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              {isInvitingBatch ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('promoters.list.inviting')}</span>
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  <span>{t('promoters.list.inviteSelected', { count: selectedImported.length })}</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="p-6">
        {/* Section des filtres */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Filter className="w-5 h-5 text-gray-500" />
                <h3 className="text-lg font-semibold text-gray-900">{t('common.filters')}</h3>
                {hasActiveFilters && (
                  <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2 py-1 rounded-full">
                    {t('promoters.list.activeFiltersCount', {
                      count: [
                        filters.companySize ? 1 : 0,
                        filters.sectors.length,
                        filters.company ? 1 : 0,
                        filters.status ? 1 : 0,
                        repFilter ? 1 : 0
                      ].reduce((a, b) => a + b, 0)
                    })}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-gray-600 hover:text-gray-800 flex items-center space-x-1"
                  >
                    <X className="w-4 h-4" />
                    <span>{t('common.clear')}</span>
                  </button>
                )}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center space-x-1 text-purple-600 hover:text-purple-800"
                >
                  <span className="text-sm font-medium">
                    {showFilters ? t('promoters.list.hideFilters') : t('promoters.list.showFilters')}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>
          </div>

          {showFilters && (
            <div className="p-4 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                {/* Filtre par taille d'entreprise */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('promoters.list.companySize')}
                  </label>
                  <select
                    value={filters.companySize}
                    onChange={(e) => handleFilterChange('companySize', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">{t('promoters.list.allSizes')}</option>
                    {companySizeOptions.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>

                {/* Filtre par entreprise */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('common.company')}
                  </label>
                  <select
                    value={filters.company}
                    onChange={(e) => handleFilterChange('company', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">{t('promoters.list.allCompanies')}</option>
                    {companyOptions.map(company => (
                      <option key={company} value={company}>{company}</option>
                    ))}
                  </select>
                </div>

                {/* Filtre par statut */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('common.status')}
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">{t('promoters.list.allStatuses')}</option>
                    {statusOptions.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>

                {/* Filtre par rep */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('promoters.list.rep', 'Rep')}
                  </label>
                  <select
                    value={repFilter}
                    onChange={(e) => setRepFilter(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">{t('promoters.list.allReps', 'All reps')}</option>
                    {teamMembersData.map(m => (
                      <option key={m.id} value={m.id}>{m.firstName} {m.lastName}</option>
                    ))}
                  </select>
                </div>

                {/* Compteur de résultats */}
                <div className="flex items-end">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 w-full">
                    <div className="text-sm text-gray-600">{t('common.results')}</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {filteredItems.length}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t('promoters.list.ofTotal', { total: allItems.length })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtre par secteurs d'activité */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t('promoters.list.industrySectorsSelected', { count: filters.sectors.length })}
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {sectorOptions.map(sector => (
                    <button
                      key={sector}
                      onClick={() => handleSectorToggle(sector)}
                      className={`p-2 text-xs rounded-lg border transition-all duration-200 ${
                        filters.sectors.includes(sector)
                          ? 'bg-purple-100 border-purple-300 text-purple-800 font-medium'
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'
                      }`}
                    >
                      {sector}
                    </button>
                  ))}
                </div>
                {filters.sectors.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {filters.sectors.map(sector => (
                      <span
                        key={sector}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                      >
                        {sector}
                        <button
                          onClick={() => handleSectorToggle(sector)}
                          className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* View mode toggle */}
        <div className="flex justify-end mb-4">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-[#4339A0] text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid3x3 className="w-4 h-4" />
              <span className="text-sm font-medium">{t('promoters.list.gridView')}</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'table'
                  ? 'bg-[#4339A0] text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4" />
              <span className="text-sm font-medium">{t('promoters.list.tableView')}</span>
            </button>
          </div>
        </div>

        {/* Résultats */}
        {filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <Filter className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('promoters.list.noPromotersFound')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('promoters.list.noPromotersMatch')}
            </p>
            <button
              onClick={clearFilters}
              className="btn-primary"
            >
              {t('promoters.list.clearFilters')}
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
            {filteredItems.map((item) => {
              if (item._type === 'imported') {
                const contact = item as ListItem & { _type: 'imported' } & ImportedContact;
                const isLoading = invitingId === contact.id;
                return (
                  <div
                    key={`imported-${contact.id}`}
                    className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300 hover:border-gray-400 transition-all duration-200"
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <Avatar
                        src={undefined}
                        name={contact.name}
                        size="md"
                        className="border-2 border-white shadow-sm"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">{contact.name}</h3>
                        <p className="text-xs text-gray-600 truncate flex items-center">
                          <Building className="w-3 h-3 mr-1 flex-shrink-0" />
                          {contact.company || '—'}
                        </p>
                      </div>
                      <button
                        onClick={() => toggleSelectImported(contact.id)}
                        className="flex-shrink-0 text-gray-400 hover:text-[#4339A0]"
                      >
                        {selectedImported.includes(contact.id)
                          ? <CheckSquare className="w-5 h-5 text-[#4339A0]" />
                          : <Square className="w-5 h-5" />
                        }
                      </button>
                    </div>

                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      {getStatusBadge('imported')}
                      <ImportSourceBadge source={contact.importSource} />
                      {contact.assignedTeamMemberName && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
                          <Users className="w-3 h-3 mr-1" />
                          {contact.assignedTeamMemberName}
                        </span>
                      )}
                    </div>

                    <div className="space-y-2 mb-3">
                      {contact.companySize && (
                        <div className="flex items-center text-xs text-gray-600">
                          <Users className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{contact.companySize}</span>
                        </div>
                      )}
                      {contact.industrySectors.length > 0 && (
                        <div className="flex items-start text-xs text-gray-600">
                          <Building className="w-3 h-3 mr-1 flex-shrink-0 mt-0.5" />
                          <div className="flex flex-wrap gap-1 min-w-0">
                            {contact.industrySectors.slice(0, 2).map((sector, index) => (
                              <span
                                key={index}
                                className="inline-block px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium truncate"
                              >
                                {sector}
                              </span>
                            ))}
                            {contact.industrySectors.length > 2 && (
                              <span className="text-xs text-gray-500">+{contact.industrySectors.length - 2}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="pt-3 border-t border-gray-200">
                      <button
                        onClick={(e) => handleInviteSingle(e, contact.id)}
                        disabled={isLoading || isInvitingSingle}
                        className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-[#4339A0] text-white rounded-lg text-sm font-medium hover:bg-[#3d2f8f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>{t('promoters.list.inviting')}</span>
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4" />
                            <span>{t('promoters.list.invite')}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              }

              const promoter = item as ListItem & { _type: 'promoter' } & Promoter;
              const sectors = getPromoterSectors(promoter.id);
              const companySize = getCompanySize(promoter.id);

              return (
                <div
                  key={promoter.id}
                  className="bg-gray-50 rounded-lg p-4 hover:shadow-md transition-all duration-200 cursor-pointer hover:bg-gray-100 border border-gray-200"
                  onClick={() => handlePromoterClick(promoter.id)}
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <Avatar
                      src={promoter.avatar}
                      name={promoter.name}
                      size="md"
                      className="border-2 border-white shadow-sm"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{promoter.name}</h3>
                      <p className="text-xs text-gray-600 truncate flex items-center">
                        <Building className="w-3 h-3 mr-1 flex-shrink-0" />
                        {promoter.company}
                      </p>
                    </div>
                  </div>

                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    {getStatusBadge(promoter.status)}
                    <ImportSourceBadge source={promoter.importSource} />
                    <WidgetBadge promoter={promoter} />
                    <ApproveWidgetAction promoter={promoter} />
                  </div>

                  {(() => {
                    const raw = promotersData?.find(p => p.id === promoter.id);
                    const assigned = raw?.assignedTeamMember;
                    const creator = raw?.createdByTeamMember;
                    const samePersonOrSingle = assigned?.id === creator?.id;
                    if (!assigned && !creator) return null;
                    return (
                      <div className="mb-3 space-y-0.5">
                        {assigned && (
                          <div className="flex items-center space-x-1.5">
                            <Avatar src={assigned.avatar} name={`${assigned.firstName} ${assigned.lastName}`} size="sm" />
                            <span className="text-xs font-semibold text-gray-900 truncate">
                              {assigned.firstName} {assigned.lastName}
                            </span>
                          </div>
                        )}
                        {creator && !samePersonOrSingle && (
                          <div className="text-xs text-gray-500 truncate pl-1">
                            {t('promoters.list.invitedByName', 'Invited by')} {creator.firstName} {creator.lastName}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <div className="space-y-2 mb-3">
                    <div className="flex items-center text-xs text-gray-600">
                      <Users className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{companySize}</span>
                    </div>
                    <div className="flex items-start text-xs text-gray-600">
                      <Building className="w-3 h-3 mr-1 flex-shrink-0 mt-0.5" />
                      <div className="flex flex-wrap gap-1 min-w-0">
                        {sectors.slice(0, 2).map((sector, index) => (
                          <span
                            key={index}
                            className="inline-block px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium truncate"
                          >
                            {sector}
                          </span>
                        ))}
                        {sectors.length > 2 && (
                          <span className="text-xs text-gray-500">+{sectors.length - 2}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-3 border-t border-gray-200">
                    <div className="text-center">
                      <div className="flex items-center justify-center mb-1">
                        <TrendingUp className="w-3 h-3 text-blue-500 mr-1" />
                        <span className="text-sm font-semibold text-gray-900">
                          {promoter.prospectsReferred}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600">{t('common.prospects')}</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center mb-1">
                        <Award className="w-3 h-3 text-green-500 mr-1" />
                        <span className="text-sm font-semibold text-gray-900">
                          {promoter.conversionRate}%
                        </span>
                      </div>
                      <p className="text-xs text-gray-600">{t('common.conversion')}</p>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">{t('common.rewards')}:</span>
                      <span className="font-semibold text-green-600">
                        {promoter.totalRewards.toLocaleString()}€
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 w-10"></th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('promoters.list.columns.promoter')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.company')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('promoters.list.columns.source')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('promoters.list.columns.assignedTo')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('promoters.list.columns.size')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('promoters.list.columns.sectors')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.prospects')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.conversion')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.rewards')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.action')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredItems.map((item) => {
                    if (item._type === 'imported') {
                      const contact = item as ListItem & { _type: 'imported' } & ImportedContact;
                      const isLoading = invitingId === contact.id;
                      return (
                        <tr key={`imported-${contact.id}`} className="bg-gray-50/50 hover:bg-gray-100 transition-colors">
                          <td className="px-4 py-4">
                            <button
                              onClick={() => toggleSelectImported(contact.id)}
                              className="text-gray-400 hover:text-[#4339A0]"
                            >
                              {selectedImported.includes(contact.id)
                                ? <CheckSquare className="w-5 h-5 text-[#4339A0]" />
                                : <Square className="w-5 h-5" />
                              }
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <Avatar src={undefined} name={contact.name} size="md" />
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                                <div className="text-sm text-gray-500">{contact.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <Building className="w-4 h-4 mr-2 text-gray-400" />
                              {contact.company || '—'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge('imported')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <ImportSourceBadge source={contact.importSource} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {contact.assignedTeamMemberName ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                {contact.assignedTeamMemberName}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {contact.companySize || '—'}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {contact.industrySectors.slice(0, 3).map((sector, index) => (
                                <span key={index} className="inline-block px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                  {sector}
                                </span>
                              ))}
                              {contact.industrySectors.length > 3 && (
                                <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                                  +{contact.industrySectors.length - 3}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center text-sm text-gray-400">—</td>
                          <td className="px-6 py-4 text-center text-sm text-gray-400">—</td>
                          <td className="px-6 py-4 text-right text-sm text-gray-400">—</td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={(e) => handleInviteSingle(e, contact.id)}
                              disabled={isLoading || isInvitingSingle}
                              className="inline-flex items-center space-x-1 px-3 py-1.5 bg-[#4339A0] text-white rounded-lg text-xs font-medium hover:bg-[#3d2f8f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isLoading ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Mail className="w-3 h-3" />
                              )}
                              <span>{isLoading ? t('promoters.list.inviting') : t('promoters.list.invite')}</span>
                            </button>
                          </td>
                        </tr>
                      );
                    }

                    const promoter = item as ListItem & { _type: 'promoter' } & Promoter;
                    const sectors = getPromoterSectors(promoter.id);
                    const companySize = getCompanySize(promoter.id);

                    return (
                      <tr
                        key={promoter.id}
                        onClick={() => handlePromoterClick(promoter.id)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-4"></td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Avatar
                              src={promoter.avatar}
                              name={promoter.name}
                              size="md"
                            />
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {promoter.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {promoter.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-900">
                            <Building className="w-4 h-4 mr-2 text-gray-400" />
                            {promoter.company}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(promoter.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <ImportSourceBadge source={promoter.importSource} />
                            <WidgetBadge promoter={promoter} />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {(() => {
                            const raw = promotersData?.find(p => p.id === promoter.id);
                            const assigned = raw?.assignedTeamMember;
                            const creator = raw?.createdByTeamMember;
                            const samePersonOrSingle = assigned?.id === creator?.id;
                            if (!assigned && !creator) return <span className="text-gray-400">—</span>;
                            return (
                              <div className="space-y-0.5">
                                {assigned && (
                                  <div className="flex items-center space-x-1.5">
                                    <Avatar src={assigned.avatar} name={`${assigned.firstName} ${assigned.lastName}`} size="sm" />
                                    <span className="font-medium text-gray-900 text-xs">
                                      {assigned.firstName} {assigned.lastName}
                                      {samePersonOrSingle && creator ? ` (${t('promoters.list.invitedBy', 'invited by')} ${creator.firstName})` : ''}
                                    </span>
                                  </div>
                                )}
                                {creator && !samePersonOrSingle && (
                                  <div className="text-xs text-gray-500">
                                    {t('promoters.list.invitedByName', 'Invited by')} {creator.firstName} {creator.lastName}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-600">
                            <Users className="w-4 h-4 mr-2 text-gray-400" />
                            {companySize}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {sectors.slice(0, 3).map((sector, index) => (
                              <span
                                key={index}
                                className="inline-block px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium"
                              >
                                {sector}
                              </span>
                            ))}
                            {sectors.length > 3 && (
                              <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                                +{sectors.length - 3}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center">
                            <TrendingUp className="w-4 h-4 text-blue-500 mr-1" />
                            <span className="text-sm font-semibold text-gray-900">
                              {promoter.prospectsReferred}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center">
                            <Award className="w-4 h-4 text-green-500 mr-1" />
                            <span className="text-sm font-semibold text-gray-900">
                              {promoter.conversionRate}%
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-semibold text-green-600">
                            {promoter.totalRewards.toLocaleString()}€
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <ApproveWidgetAction promoter={promoter} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <AssignImportedContactsModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        onAssign={handleAssignToMember}
        selectedContactCount={selectedImported.length}
        isLoading={isAssigning}
      />
    </>
  );
};

export default PromotersList;
